import AxiosProvider from '@/app/utils/api/axios-provider';
import { AxiosResponse } from 'axios';
import { AttributeLockApiResponse } from '@/app/shared/types/stores/use-attribute-locker-api.resp.interface';
import { CheckCodeSyntaxPayload } from '@/app/shared/types/params/code-meta-information-params.interface';
import { PaginationParams } from '@/app/shared/types/params/request-params.interface';
import { OverviewDataEvent } from '@/app/shared/types/events/overview-data-event.interface';

export default {
  getAttributes(
    clientId: number,
    queryParams: PaginationParams
  ): Promise<AxiosResponse> {
    return AxiosProvider.amsApiClient.get(`/clients/${clientId}/attributes`, {
      params: { ...queryParams },
    });
  },
  updateAttribute(
    clientId: number,
    attributeId: number | string,
    // TODO: Create an interface for this attribute or use an existing one
    attribute: any
  ): Promise<AxiosResponse> {
    return AxiosProvider.amsApiClient.patch(
      `/clients/${clientId}/attributes/${attributeId}`,
      attribute
    );
  },
  createAttribute(clientId: number, attribute: any): Promise<AxiosResponse> {
    return AxiosProvider.amsApiClient.post(
      `/clients/${clientId}/attributes`,
      attribute
    );
  },
  getAttributeById(
    clientId: number,
    attributeId: number | string
  ): Promise<AxiosResponse> {
    return AxiosProvider.amsApiClient.get(
      `/clients/${clientId}/attributes/${attributeId}`
    );
  },
  deleteAttributes(
    clientId: number,
    attributesId?: Array<number>
  ): Promise<unknown> {
    return AxiosProvider.amsApiClient.delete(
      `/clients/${clientId}/attributes`,
      {
        data: attributesId,
      }
    );
  },
  getWhereUsedByAttributeId(clientId: number, attributeId: number | string) {
    return AxiosProvider.amsApiClient.get(
      `/clients/${clientId}/attributes/${attributeId}/where-used`
    );
  },
  getAttributesFiltersFunctionsByClientId(clientId: number): Promise<unknown> {
    return AxiosProvider.amsApiClient.get(
      `/clients/${clientId}/lua-coding-references`
    );
  },
  lockAttribute(
    clientId: number,
    // TODO: Create an interface for this attribute or use an existing one
    attributeId: any,
    isAutoLock: boolean
  ): Promise<unknown> {
    return AxiosProvider.amsApiClient.post(
      `/clients/${clientId}/attributes/${attributeId}/lock?isAutoLock=${isAutoLock}`
    );
  },
  unlockAttribute(
    clientId: number,
    // TODO: Create an interface for this attribute or use an existing one
    attributeId: any,
    isAutoLock: boolean
  ): Promise<unknown> {
    return AxiosProvider.amsApiClient.post(
      `/clients/${clientId}/attributes/${attributeId}/unlock?isAutoLock=${isAutoLock}`
    );
  },
  refreshLockAttributeById(
    clientId: number,
    attributeId: number
  ): Promise<AttributeLockApiResponse> {
    return AxiosProvider.amsApiClient.post(
      `/clients/${clientId}/attributes/${attributeId}/refresh-lock`
    );
  },
  getHistoryByAttributeId(
    clientId: number,
    attributeId: number | string,
    queryParams: PaginationParams
  ): Promise<AxiosResponse> {
    return AxiosProvider.amsApiClient.get(
      `/clients/${clientId}/attributes/${attributeId}/history`,
      {
        params: { ...queryParams },
        paramsSerializer: { indexes: null },
      }
    );
  },
  checkCodeSyntax(
    clientId: number,
    payload: CheckCodeSyntaxPayload
  ): Promise<unknown> {
    return AxiosProvider.amsApiClient.post(
      `/clients/${clientId}/attributes/check-syntax`,
      payload
    );
  },
  saveAttributeCode(
    clientId: number,
    attributeId: number,
    // TODO: Create an interface for this attribute or use an existing one
    codeEntity: any
  ): Promise<unknown> {
    return AxiosProvider.amsApiClient.post(
      `/clients/${clientId}/attributes/${attributeId}/save-code`,
      codeEntity
    );
  },
  rollbackAttributeCode(
    clientId: number,
    revisionId: number
  ): Promise<AttributeLockApiResponse> {
    return AxiosProvider.amsApiClient.post(
      `/clients/${clientId}/attributes/rollback/${revisionId}`
    );
  },
  validateAttributeCode(
    clientId: number,
    attributeId: number,
    dataSourceName: any,
    file: FormData
  ) {
    return AxiosProvider.amsApiClient.post(
      `/clients/${clientId}/attributes/${attributeId}/validate?dsName=${dataSourceName}`,
      file,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },
  copyAttributeById(
    attribute: OverviewDataEvent,
    clientId: number,
    attributeId: number | string
  ): Promise<AxiosResponse> {
    return AxiosProvider.amsApiClient.post(
      `/clients/${clientId}/attributes/${attributeId}/copy`,
      { ...attribute }
    );
  },
  lockAllAttributes(clientId: number): Promise<unknown> {
    return AxiosProvider.amsApiClient.post(
      `/clients/${clientId}/ATTRIBUTE/lock`
    );
  },
  unlockAllAttributes(clientId: number): Promise<unknown> {
    return AxiosProvider.amsApiClient.post(
      `/clients/${clientId}/ATTRIBUTE/unlock`
    );
  },
  releaseAllEditingLocksByClientId(clientId: number): Promise<unknown> {
    return AxiosProvider.amsApiClient.post(
      `/clients/${clientId}/unlock-editable-locks`
    );
  },
  checkDuplicateShortName(clientId: number, name: string) {
    return AxiosProvider.amsApiClient.get(
      `/clients/${clientId}/attributes/${name}/check`
    );
  },

  retrieveCyclicDependencies(clientId: number, selectedIds?: Array<number>) {
    return AxiosProvider.amsApiClient.get(
      `/clients/${clientId}/attributes/cyclic-dependencies`,
      {
        params: {
          entityIds: selectedIds?.join(','),
        },
      }
    );
  },
};
